import { ApiAsset } from '@/components/app/cards/PacksList/types';
import { useUserState } from '@/hooks/store';
import { Guide } from '@/store/guide/types';
import { MeditationType, Program } from '@/store/program/types';

const ALWAYS_AVAILABLE_TYPES = new Set(['music', 'wisdom', 'meditation_with_music', 'timer']);

export enum GuideState {
	CourseCompleted = 'course_completed',
	CourseUnlocked = 'course_unlocked',
	CourseDisabled = 'course_disabled',
	CourseLocked = 'course_locked',
	Unlocked = 'unlocked',
	Locked = 'locked',
}

interface UseGuideInfo {
	(
		guide: Guide,
		program: Program,
		programProgress?: number,
	): {
		guideState: GuideState;
		title: string;
		description?: string;
		isCourse: boolean;
		isAvailable: boolean;
		isDisabled: boolean;
		image?: ApiAsset;
	};
}

export const useGuideInfo: UseGuideInfo = (guide, program, programProgress = 0) => {
	const user = useUserState();

	if (!guide) {
		return {
			title: '',
			guideState: GuideState.Locked,
			isCourse: false,
			isAvailable: false,
			isDisabled: false,
		};
	}

	const getGuideState = (): GuideState => {
		if (program?.content_type === 'Course') {
			if (guide.position <= programProgress) {
				return GuideState.CourseCompleted;
			}
			if (guide.position <= programProgress + 1) {
				return GuideState.CourseUnlocked;
			}
			if (user?.subscription?.valid) {
				return GuideState.CourseDisabled;
			}
			return GuideState.CourseLocked;
		}

		if (ALWAYS_AVAILABLE_TYPES.has(program?.meditation_type as string)) {
			return GuideState.Unlocked;
		}

		if (guide?.is_free) {
			return GuideState.Unlocked;
		}
		return GuideState.Locked;
	};

	const guideState = getGuideState();

	const isCourse = guideState.includes('course');
	const isAvailable = guideState.includes('unlocked') || guideState.includes('completed');
	const isDisabled = guideState.includes('disabled');

	const image = guide.cell_image ?? program?.guides?.[0]?.cell_image ?? program?.background_image;

	// Android decides this line per program type rather than showing whatever the wire carries:
	// a sequential program's steps and a meditation-with-music track have no room for a second
	// line, and a music program credits the narrator there instead of repeating a track blurb.
	const getGuideDescription = (): string | undefined => {
		if (
			program?.meditation_type === MeditationType.Sequential ||
			program?.meditation_type === MeditationType.MeditationWithMusic
		) {
			return undefined;
		}

		if (program?.meditation_type === MeditationType.Music) {
			return guide.narrator_name?.trim() || undefined;
		}

		// Some subtitles arrive with a leading space (" It's not you, it's me."), which shows up
		// as a visible indent against the title above it.
		return guide.subtitle?.trim() || undefined;
	};

	const getGuideTitle = () => {
		if (isCourse) {
			return `${guide.position}. ${guide.title}`;
		}

		if (program.meditation_type === MeditationType.MasterClass) {
			return `${guide.position - 1}. ${guide.title}`;
		}

		return guide.title;
	};

	return {
		title: getGuideTitle(),
		description: getGuideDescription(),
		isCourse,
		isAvailable,
		isDisabled,
		guideState,
		image,
	};
};
